import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { Endpoints } from "../../../Lib/endpoints";
import {
  PerformRequest,
  usePerformRequest,
} from "../../../Lib/usePerformRequest";
import { BlogProps, BlogCommentProps } from "../../../Lib/types";
import { NonPaginatedResponse } from "../../../Lib/responses";
import "./styles.scss";
import {
  Button,
  Group,
  Paper,
  Text,
  Avatar,
  Divider,
  Stack,
  Container,
  Badge,
  Anchor,
  Modal,
  ActionIcon,
  Tooltip,
} from "@mantine/core";
import {
  IconEdit,
  IconMessage,
  IconHeart,
  IconTrash,
  IconAlertTriangle,
} from "@tabler/icons-react";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { HttpSuccessCodesObj } from "../../../Lib/data";
import {
  DefaultSuccessNotification,
  DefaultErrorNotification,
} from "../../../Lib/methods";
import { useDisclosure } from "@mantine/hooks";

dayjs.extend(relativeTime);

const SingleBlogView: React.FC = () => {
  const { blogID } = useParams();

  const {
    data: blog,
    isLoading: isLoadingBlog,
    reloadData: reloadBlog,
  } = usePerformRequest<NonPaginatedResponse<BlogProps>>({
    method: Endpoints.GetSingleBlogDetails(blogID as string).method,
    route: Endpoints.GetSingleBlogDetails(blogID as string).route,
  });

  const [isDeletingComment, setDeletingComment] = useState(false);
  const [selectedComment, setSelectedComment] =
    useState<BlogCommentProps | null>(null);

  const [
    isDeleteModalOpened,
    { open: OpenDeleteModal, close: CloseDeleteModal },
  ] = useDisclosure(false);

  const handleDeleteClick = (comment: BlogCommentProps) => {
    setSelectedComment(comment);
    OpenDeleteModal();
  };

  const DeleteBlogComment = async () => {
    if (!selectedComment) return;

    setDeletingComment(true);
    const r = await PerformRequest({
      method: Endpoints.GetDeleteBlogComment(blogID as string).method,
      route: Endpoints.GetDeleteBlogComment(blogID as string).route,
      data: {
        commentID: selectedComment.id,
      },
      callback: () => {
        setDeletingComment(false);
      },
    });

    setDeletingComment(false);
    if (r) {
      if (r.status === HttpSuccessCodesObj.Success) {
        DefaultSuccessNotification("Comment has been removed!");
        CloseDeleteModal();
        setSelectedComment(null);
        reloadBlog(); // Reload blog data to refresh comments
      } else {
        DefaultErrorNotification(r.message || "Could not delete comment!");
      }
    }
  };

  const CancelDelete = () => {
    CloseDeleteModal();
    setSelectedComment(null);
  };

  if (isLoadingBlog) {
    return <div>Loading...</div>;
  }

  if (!blog?.data) {
    return <div>Blog not found</div>;
  }

  return (
    <div
      className="single-blog-view-container"
      style={{ background: "#f8f9fa", minHeight: "100vh", padding: 32 }}
    >
      <Group justify="space-between" align="center" mb={24}>
        <h1 style={{ margin: 0 }}>{blog.data.title}</h1>
        <Link to={`/d/blog-edit/${blogID}`} style={{ textDecoration: "none" }}>
          <Button leftSection={<IconEdit size={16} />} color="grape" radius={8}>
            Edit
          </Button>
        </Link>
      </Group>

      {/* Blog Content */}
      <div
        className="blog-content ProseMirror"
        dangerouslySetInnerHTML={{ __html: blog.data.content }}
        style={{
          background: "#fff",
          borderRadius: 8,
          padding: 32,
          boxShadow: "0 2px 12px 0 rgba(80,60,180,0.06)",
          marginTop: 0,
          marginBottom: 32,
        }}
      />

      {/* Comments Section */}
      <Container size="lg" px={0}>
        <Paper
          style={{
            background: "#fff",
            borderRadius: 12,
            padding: 0,
            boxShadow: "0 2px 12px 0 rgba(80,60,180,0.06)",
            overflow: "hidden",
          }}
        >
          {/* Comments Header */}
          <div
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              padding: "20px 24px",
              color: "white",
            }}
          >
            <Group align="center" gap={12}>
              <IconMessage size={24} />
              <Text size="lg" fw={600}>
                Comments ({blog.data.comments?.length || 0})
              </Text>
            </Group>
          </div>

          {/* Comments List */}
          <div style={{ padding: "24px" }}>
            {blog.data.comments && blog.data.comments.length > 0 ? (
              <Stack gap={24}>
                {blog.data.comments.map((comment, index) => (
                  <div key={comment.id || index}>
                    <Group align="flex-start" gap={12}>
                      <Avatar
                        radius="xl"
                        size={44}
                        style={{
                          border: "2px solid #e9ecef",
                        }}
                      />
                      <div style={{ flex: 1 }}>
                        <Paper
                          style={{
                            background: "#f8f9fa",
                            padding: "16px 20px",
                            borderRadius: 16,
                            border: "1px solid #e9ecef",
                            position: "relative",
                          }}
                        >
                          {/* Delete Button */}
                          <div
                            style={{
                              position: "absolute",
                              top: 12,
                              right: 12,
                            }}
                          >
                            <Tooltip label="Delete comment" position="left">
                              <ActionIcon
                                variant="subtle"
                                color="red"
                                size="sm"
                                onClick={() => handleDeleteClick(comment)}
                                style={{
                                  opacity: 0.7,
                                  transition: "opacity 0.2s",
                                }}
                                onMouseEnter={(e) =>
                                  (e.currentTarget.style.opacity = "1")
                                }
                                onMouseLeave={(e) =>
                                  (e.currentTarget.style.opacity = "0.7")
                                }
                              >
                                <IconTrash size={14} />
                              </ActionIcon>
                            </Tooltip>
                          </div>

                          <Group
                            justify="space-between"
                            align="center"
                            mb={8}
                            pr={32}
                          >
                            <Group align="center" gap={8}>
                              <Text fw={600} size="sm" c="#495057">
                                {comment.name}
                              </Text>
                              <Badge
                                variant="light"
                                color="blue"
                                size="xs"
                                radius="xl"
                              >
                                Student
                              </Badge>
                            </Group>
                            <Text size="xs" c="dimmed">
                              {dayjs(comment.createdAt).fromNow()}
                            </Text>
                          </Group>
                          <Text
                            size="sm"
                            style={{ lineHeight: 1.6, paddingRight: 24 }}
                          >
                            {comment.content}
                          </Text>
                        </Paper>
                      </div>
                    </Group>
                    {index < blog.data.comments.length - 1 && (
                      <Divider my={20} color="#f1f3f4" />
                    )}
                  </div>
                ))}
              </Stack>
            ) : (
              <div
                style={{
                  textAlign: "center",
                  padding: "48px 24px",
                  color: "#adb5bd",
                }}
              >
                <IconMessage
                  size={48}
                  style={{ marginBottom: 16, opacity: 0.5 }}
                />
                <Text size="lg" fw={500} mb={8}>
                  No comments yet
                </Text>
                <Text size="sm" c="dimmed">
                  Be the first to share your thoughts on this blog post!
                </Text>
              </div>
            )}
          </div>

          {/* Comments Footer */}
          {blog.data.comments && blog.data.comments.length > 0 && (
            <div
              style={{
                background: "#f8f9fa",
                padding: "16px 24px",
                borderTop: "1px solid #e9ecef",
                textAlign: "center",
              }}
            >
              <Group justify="center" align="center" gap={8}>
                <IconHeart size={16} style={{ color: "#fa5252" }} />
                <Text size="sm" c="dimmed">
                  {blog.data.likes?.length || 0} people liked this post
                </Text>
              </Group>
            </div>
          )}
        </Paper>
      </Container>

      {/* Delete Confirmation Modal */}
      <Modal
        opened={isDeleteModalOpened}
        onClose={CancelDelete}
        title={
          <Group align="center" gap={8}>
            <IconAlertTriangle size={20} color="#fa5252" />
            <Text fw={600} size="lg">
              Delete Comment
            </Text>
          </Group>
        }
        centered
        radius={12}
        overlayProps={{
          backgroundOpacity: 0.55,
          blur: 3,
        }}
        styles={{
          header: {
            paddingBottom: 0,
          },
          body: {
            paddingTop: 16,
          },
        }}
      >
        <Stack gap={20}>
          <div>
            <Text size="sm" c="dimmed" mb={12}>
              Are you sure you want to delete this comment? This action cannot
              be undone.
            </Text>

            {selectedComment && (
              <Paper
                style={{
                  background: "#f8f9fa",
                  padding: 16,
                  borderRadius: 8,
                  border: "1px solid #e9ecef",
                }}
              >
                <Group align="center" gap={8} mb={8}>
                  <Avatar size={24} radius="xl" />
                  <Text fw={500} size="sm">
                    {selectedComment.name}
                  </Text>
                  <Text size="xs" c="dimmed">
                    • {dayjs(selectedComment.createdAt).fromNow()}
                  </Text>
                </Group>
                <Text size="sm" style={{ lineHeight: 1.5 }}>
                  "{selectedComment.content}"
                </Text>
              </Paper>
            )}
          </div>

          <Group justify="flex-end" gap={12}>
            <Button
              variant="outline"
              color="gray"
              onClick={CancelDelete}
              disabled={isDeletingComment}
              radius={8}
            >
              Cancel
            </Button>
            <Button
              color="red"
              onClick={DeleteBlogComment}
              loading={isDeletingComment}
              leftSection={<IconTrash size={16} />}
              radius={8}
            >
              {isDeletingComment ? "Deleting..." : "Delete Comment"}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
};

export default SingleBlogView;
